import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import OrderDetailRow from "../../components/form/OrderDetailRow";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import DefaultSelect from "../../components/form/DefaultSelect";
import { Alert, Button, Divider, List, Modal, Spin } from "antd";
import { ColDef } from "ag-grid-community";
import api from "../../utils/axios";
import {
	ExclamationCircleFilled,
	FormOutlined,
	MinusCircleOutlined,
	CheckCircleOutlined,
	CloseOutlined,
} from "@ant-design/icons";
import {
	codeFormatter,
	formatMoney,
	priceFormatter,
	priceFormatterByCurrency,
	valueDateFormatter,
} from "../../utils/formatters";
import { dualMoneyColumns } from "../../utils/moneyColumns";
import { toast } from "react-toastify";
import { SettlementOrderPaymentType } from "../../types/SettlementOrderPaymentType";
import classes from "./ClientDetails.module.css";
import OrderStatus from "../../components/OrderStatus";
import { ReactComponent as CodeIcon } from "../../assets/codeinput.svg";
import { ReactComponent as SearchIcon } from "../../assets/search-find.svg";
import { ReactComponent as ChevrovDown } from "../../assets/chevrovDown.svg";
import PaginationTable from "../../components/Pagination";
import CurrencyBalanceCard from "../../components/CurrencyBalanceCard";
import DefaultInput from "../../components/form/DefaultInput";
import { ResponsiveContainer, PieChart, Cell, Pie } from "recharts";
import { validatePassword } from "../../utils/passwordValidation";
import { ReactComponent as QrCodeIcon } from "../../assets/qr-code.svg";
import {
	documentTypes,
	orderStatuses,
	settlementStatuses,
} from "../../types/enums";
import {
	fetchSettlementDisableRequestValidation,
	fetchSettlementValidationForInfo,
} from "../../lib/SettlementValidationApi";
import { AxiosResponse } from "axios";
import useLang from "../../hooks/useLang";
import {
	fetchUserCashRegister,
	getCashRegisters,
	getConfigurationValue,
} from "../../lib/OptionListApi";
import { ReactComponent as ExportIcon } from "../../assets/arrow-download-small.svg";
import {
	exportDataToCsv,
	moneyExportColumns,
} from "../../utils/exportDataToCsv";
import { orderStatistic } from "../../redux/orderSlice";
import { useDispatch } from "react-redux";
import { fetchZones } from "../../lib/OptionListApi";
import { Select, MenuItem, useMediaQuery, useStepContext } from "@mui/material";
import Map from "../Dashboard/Map";
import { hasAccess } from "../../lib/MenuValidation";
import { CONFIG } from "../../config";
import useCurrencies from "../../hooks/useCurrencies";
import { currencySymbol } from "../../utils/formatters";
import SettlementBreakdown, {
	SettlementBreakdownColumn,
} from "./SettlementBreakdown";
import { CURRENCY_EUR, CURRENCY_LEK } from "../../api/currency";
import PlaceAutocomplete from "../../components/GoogleMaps/PlaceAutocomplete";
import type ClientDetailsInterface from "../../types/ClientDetailsInterface";
import { ClientBalance } from "../../types/ClientBalance";

const initialTableData = [
	{
		country: "Kosovo",
		orderCount: 0,
	},
	{
		country: "Albania",
		orderCount: 0,
	},
	{
		country: "Macedonia",
		orderCount: 0,
	},
];

/** Money rounds to two decimals; nothing here touches a rate, so nothing else rounds. */
const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * The client's unpaid refusal/exchange debt, one total per currency. Reads the API's own split
 * (`value` / `valueEur`); off PostmanAL `valueEur` is absent and `eur` stays 0.
 */
const sumEarningTotals = (rows: any[] | undefined) =>
	(Array.isArray(rows) ? rows : []).reduce(
		(acc, row) => ({
			local: round2(acc.local + Number(row?.value ?? 0)),
			eur: round2(acc.eur + Number(row?.valueEur ?? 0)),
		}),
		{ local: 0, eur: 0 }
	);

const ClientDetails = () => {
	const { strings } = useLang("clientDetails");

	const initialStatusData = [
		{
			id: 1,
			description: strings?.registered,
			numberOfOrders: 0,
		},
		{
			id: 5,
			description: strings?.transporting,
			numberOfOrders: 0,
		},
		{
			id: 2,
			description: strings?.waitingPickup,
			numberOfOrders: 0,
		},
		{
			id: 4,
			description: strings?.inStorage,
			numberOfOrders: 0,
		},
		{
			id: 6,
			description: strings?.delivered,
			numberOfOrders: 0,
		},
		{
			id: 10,
			description: strings?.returned,
			numberOfOrders: 0,
		},
		{
			id: 7,
			description: strings?.rejected,
			numberOfOrders: 0,
		},
	];
	const dispatch = useDispatch();
	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);
	const navigate = useNavigate();
	const location = useLocation();
	const urlParams = new URLSearchParams(window.location.search);
	const clientId = urlParams.get("id");

	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [bankPaymentTableColumns, setBankPaymentTableColumns] =
		useState<ColDef[]>();
	const [settlementOrderTableColumns, setSettlementOrderTableColumns] =
		useState<ColDef[]>();
	const [name, setName] = useState<string | undefined>();
	const [lastName, setLastName] = useState<string | undefined>();
	const [businessName, setBusinessName] = useState<string | undefined>();
	const [businessNumber, setBusinessNumber] = useState<string | undefined>();
	const [businessPhoneNumber, setBusinessPhoneNumber] = useState<
		string | undefined
	>();
	const [email, setEmail] = useState<string | undefined>();
	const [username, setUsername] = useState<string | undefined>();
	const [phone, setPhone] = useState<string | undefined>();
	const [address, setAddress] = useState<string | undefined>();
	const [country, setCountry] = useState<string | undefined>();
	const [city, setCity] = useState<string | undefined>();
	const [status, setStatus] = useState<any>();
	const [createdAt, setCreatedAt] = useState<any>();
	const [orderSales, setOrderSales] = useState<number>(0.0);
	const [orderRefusal, setOrderRefusal] = useState<number>(0.0);
	const [currentBalance, setCurrentBalance] = useState<number>(0.0);
	const [transportPayment, setTranportPayment] = useState<number>(0.0);
	// The seller is settled in the currency each order was REGISTERED in, so the two
	// balances are independent and nothing is converted between them.
	const [orderSalesEur, setOrderSalesEur] = useState<number>(0.0);
	const [orderRefusalEur, setOrderRefusalEur] = useState<number>(0.0);
	const [currentBalanceEur, setCurrentBalanceEur] = useState<number>(0.0);
	const [transportPaymentEur, setTransportPaymentEur] = useState<number>(0.0);
	const { dualCurrencyEnabled, defaultCurrencyId } = useCurrencies();
	const [tableData, setTableData] = useState([]); //data type ?
	const [ordersCount, setOrdersCount] = useState(initialStatusData);
	const [ordersCountByCity, setOrdersCountByCity] = useState<any[]>([]);
	const [settlementData, setSettlementData] = useState<any[]>([]);
	const [cities, setCities] = useState<any>([]);
	const [tableOrderByCountries, setTableOrderByCountries] =
		useState(initialTableData);
	const [selectedSettlementOption, setSelectedSettlementOption] =
		useState<number>();
	const [settlementSum, setSettlementSum] = useState<number | null>();
	// The EUR half of the selection. Independent of settlementSum — each is capped by its own
	// balance and the two are paid out as separate cash-register transactions.
	const [settlementSumEur, setSettlementSumEur] = useState<number | null>();
	const [transportSum, setTransportSum] = useState<number | null>();
	const [openSettlementProcessingModal, setOpenSettlementProcessingModal] =
		useState<boolean>(false);
	const [
		openSettlementClientWithdrawnModal,
		setOpenSettlementClientWithdrawnModal,
	] = useState<boolean>(false);
	const [bankTransportPaymentModal, setBankTransportPaymentModal] =
		useState<boolean>(false);

	const [
		openSettlementOrderDetailsModal,
		setOpenSettlementOrderDetailsModal,
	] = useState<boolean>(false);
	const [settlementOrderPayments, setSettlementOrderPayments] =
		useState<SettlementOrderPaymentType>();
	let gridRef = useRef(null);
	let settlementGridRef = useRef(null);
	let settlementDetailsGridRef = useRef(null);
	let bankTransportPaymentRef = useRef(null);
	const [settlementId, setSettlementId] = useState(null);
	const [settlementDetailsId, setSettlementDetailsId] = useState(null);
	const [cashFlow, setCashFlow] = useState<any>();
	const [cashFlowEur, setCashFlowEur] = useState<number>(0.0);
	const [cashOut, setCashOut] = useState<number>();
	const [settlementOrderData, setSettlementOrderData] = useState<any>();
	const [earningDocumentsData, setEarningDocumentsData] = useState<any>();
	const [isBankPayment, setIsBankPayment] = useState<boolean>();
	const [storagePayment, setStoragePayment] = useState<boolean>();
	const [searchParams, setSearchParams] = useSearchParams();
	const [clientUrlId, setClientUrlId] = useState<any>();
	const [editable, setEditable] = useState<boolean>(false);
	const [openBlockingModal, setOpenBlockingModal] = useState<boolean>(false);
	const [password, setPassword] = useState<string>("");
	const [confirmPassword, setConfirmPassword] = useState<string>("");
	const [selectedSettlementOrders, setSelectedSettlementOrders] = useState<
		any[]
	>([]);
	const [disabledSettlementProcessing, setDisabledSettlementProcessing] =
		useState<boolean>(false);
	const [openEarningDocumentsModal, setOpenEarningDocumentsModal] =
		useState<boolean>(false);
	// Set when the earnings modal opens: the client's unpaid refusals and exchanges are always
	// deducted from the settlement and sent as earningIds. Staff can no longer opt out.
	const [earningsIncluded, setEarningsIncluded] = useState<boolean>(false);
	const [cashRegisterDropDown, setCashRegisterDropDown] = useState<any>([]);
	const [cashRegisterId, setCashRegisterId] = useState(0);
	const [cash, setCash] = useState<boolean>(true);
	const [bank, setBank] = useState<boolean>(false);
	const [percentages, setPercentages] = useState<any>([]);
	const [countries, setCountries] = useState([]);
	const [selectedCountry, setSelectedCountry] = useState<any>("");
	const [settlementDetails, setSettlementDetails] = useState([]);
	const [clientDetails, setClientDetails] = useState<any>([]);
	const [settlementDisableValidation, setSettlementDisableValidation] =
		useState<string>("");
	const [identificationNumber, setIdentificationNumber] = useState<
		string | undefined
	>();
	const [collectRegionDescription, setCollectRegionDescription] = useState<
		string | undefined
	>();
	const [personalIdLength, setPersonalIdLength] = useState<any>();

	const [sectionId, setSectionId] = useState<number | null>(null);
	const [apiLatitude, setApiLatitude] = useState<number | null>();
	const [apiLongitude, setApiLongitude] = useState<number | null>();
	const [requiresDeliveryCode, setRequiresDeliveryCode] =
		useState<boolean>(false);
	const [deliveryCodeFee, setDeliveryCodeFee] = useState<number>(0);
	const [
		uncollectedValueForDeliveryCodeFee,
		setUncollectedValueForDeliveryCodeFee,
	] = useState<number | undefined>(undefined);

	const canProcessSettlements = hasAccess("PROCESS_SETTLEMET_ACCESS");
	let autocompleteRef = useRef<any>();
	let settlementOrderGridRef = useRef(null);
	let earningDocumentsGridRef = useRef(null);

	const hexToRgba = (hex: string, alpha: number) => {
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `rgba(${r},${g},${b},${alpha})`;
	};

	const fetchConfiguration = async () => {
		const identificationNumberLength = await getConfigurationValue(3);
		setPersonalIdLength(identificationNumberLength);
	};

	const getUserCashRegisters = async () => {
		const cashRegisters = await fetchUserCashRegister();
		if (cashRegisters) {
			if (cashRegisters?.length > 1) {
				const contructedCashRegisters = cashRegisters.map(
					(item: any) => ({
						value: item.id,
						label: item.description,
					})
				);
				setCashRegisterDropDown(contructedCashRegisters);
			} else if (cashRegisters.length === 1) {
				const id = cashRegisters[0].id;
				setCashRegisterId(id);
			}
		}
	};

	const [filteredCities, setFilteredCities] = useState<any>([]);

	const handleCountryChange = (event: any) => {
		const countryId = event.target.value;
		setSelectedCountry(countryId);
		if (countryId) {
			const filtered = ordersCountByCity.filter(
				(city) => city.countryId === countryId
			);
			setFilteredCities(filtered);
		} else {
			setFilteredCities(ordersCountByCity);
		}
	};

	const [clientCountryId, setClientCountryId] = useState(null);
	const [countryCode, setCountryCode] = useState<string>("xk");

	const setInput = (data: any) => {
		setClientDetails(data);
		setSelectedCountry(data.countryId);
		setClientCountryId(data.countryId);
		setName(data.firstName);
		setLastName(data.lastName);
		setBusinessName(data.businessName);
		setEmail(data.email);
		setUsername(data.username);
		setPhone(data.phone);
		setIdentificationNumber(data.identificationNumber);
		setCollectRegionDescription(data.collectRegionDescription);
		setCountry(data.country);
		setCity(data.city);
		setAddress(data.address);
		setBusinessNumber(data.businessNumber);
		setBusinessPhoneNumber(data.businessPhoneNumber);
		setStatus(data.status);
		const createdAtDate = valueDateFormatter(data.createdAt);
		setCreatedAt(createdAtDate);
		setIsBankPayment(data.bankPackageTransportPayment);
		setStoragePayment(data.storagePayment);
		setBank(data.bankPackageTransportPayment);
		setCash(data.cashPackageTransportPayment);
		setRequiresDeliveryCode(data.requiresDeliveryCode);
		setDeliveryCodeFee(data.deliveryCodeFee);
		setSectionId(data.sectionId);
		switch (data.countryId) {
			case 1: // Kosovo
				setCountryCode("xk");
				break;
			case 2: // Albania
				setCountryCode("al");
				break;
			case 3: // North Macedonia
				setCountryCode("mk");
				break;
			default:
				break;
		}
		if (autocompleteRef?.current) {
			(autocompleteRef as any).current.setInputText(
				data?.address ? data.address : ""
			);
		}
	};

	useEffect(() => {
		if (clientCountryId) {
			handleCountryChange({ target: { value: clientCountryId } });
		} else {
			setFilteredCities(ordersCountByCity);
		}
	}, [clientCountryId, ordersCountByCity]);

	const getTableData = async (id?: any) => {
		try {
			const response = await api.get(
				"Order/fetch-orders-without-settlement",
				{
					params: { userId: clientId ? clientId : id },
				}
			);
			if (response) {
				setTableData(response.data);
			}
		} catch (error) {
			console.log(error);
			setTableData([]);
		}
	};

	const getSettlementDetails = async (id?: any) => {
		try {
			const response = await api.get(
				"Settlement/client-settlement-details",
				{
					params: { userId: clientId },
				}
			);
			if (response) {
				setSettlementDetails(response.data);
			}
		} catch (error) {
			console.log(error);
			setTableData([]);
		}
	};

	const fetchSettlements = useCallback(async (id?: any) => {
		try {
			const response = await api.get("Settlement", {
				params: { userId: clientId ? clientId : id },
			});
			if (response) {
				const settlementIdObject = response?.data?.filter(
					(item: any) => item.settlementStatusId < 3
					//  item.settlementStatusId === settlementStatuses.REQUESTED
				);
				setSettlementId(settlementIdObject[0]?.id);
				setSettlementData(response?.data);
				// checkIfSettlementCanRollback(response?.data);
			}
		} catch (error) {
			console.log(error);
		}
	}, []);

	const fetchData = useCallback(async (urlId: any | null) => {
		try {
			const response = await api.get<ClientDetailsInterface>(
				"User/client-by-id",
				{
					params: { userId: clientId ? clientId : urlId },
				}
			);
			const result = response.data;
			if (result) {
				setInput(result);
			}
		} catch (error) {
			console.error("Error fetching data:", error);
		}
	}, []);

	const getZones = async () => {
		try {
			const allZones = await fetchZones();
			if (allZones) {
				const countries = allZones
					.filter((country: any) => country.levelId === 1)
					.map((country: any) => ({
						value: country.id,
						label: country.description,
					}));
				setCountries(countries);
			} else {
				console.error("Failed to fetch zones.");
			}
		} catch (error) {
			console.error("An error occurred while fetching zones:", error);
		}
	};

	const selectClientDeliveryCodeCountAndValue = async () => {
		try {
			const res = await api.get(
				"DeliveryCode/select-delivery-code-count",
				{ params: { sellerId: clientId } }
			);
			const dataRes = res.data;
			console.log(dataRes, "dataREs");
			setUncollectedValueForDeliveryCodeFee(dataRes.uncollectedValue);
		} catch (err) {
			console.log(
				"An error occurred when selecting clients delivery code count and value: ",
				err
			);
		}
	};

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	useEffect(() => {
		const urlId = searchParams.get("id");
		setClientUrlId(urlId);
		// fetchCashRegisters();
		fetchConfiguration();
		fetchData(urlId);
		getSettlementDetails();
		getUserCashRegisters();
		selectClientDeliveryCodeCountAndValue();
		const ClientBalance = async () => {
			try {
				const response = await api.get<[ClientBalance]>(
					"Balance/client-balance",
					{
						params: { clientId: clientId ? clientId : urlId },
					}
				);
				if (response.status === 200) {
					const result = response.data[0];
					setCurrentBalance(result.currentBalance);
					setTranportPayment(result.transportationPayments);
					setOrderSales(result.salesBalance);
					setOrderRefusal(result.refusalPrice);
					setCurrentBalanceEur(result.currentBalanceEur ?? 0);
					setOrderSalesEur(result.salesBalanceEur ?? 0);
					setOrderRefusalEur(result.refusalPriceEur ?? 0);
					setTransportPaymentEur(
						result.transportationPaymentsEur ?? 0
					);
				}
			} catch (error) {
				console.log(
					"Error fetching financial balance data from the API:",
					error
				);
				setOrderSales(0.0);
				setOrderRefusal(0.0);
			}
		};
		const fetchCashFlow = async () => {
			try {
				const response = await api.get("Balance/client-cash-flow", {
					params: { clientId: clientId ? clientId : urlId },
				});
				if (response) {
					// The endpoint used to return a bare number in a one-element array
					// (`[11248946.00]`) and now returns `[{ cashFlow, cashFlowEur }]`. The old
					// code stored the whole body, which would render as "[object Object]".
					const result = response?.data?.[0];
					setCashFlow(result?.cashFlow ?? 0.0);
					setCashFlowEur(result?.cashFlowEur ?? 0.0);
				}
			} catch (error) {
				console.log(error);
				setCashFlow(0.0);
				setCashFlowEur(0.0);
			}
		};

		const fetchOrdersCount = async () => {
			try {
				const response = await api.get(
					"Order/client-order-counts-per-status",
					{
						params: {
							userId: clientId ? clientId : urlId,
						},
					}
				);
				const result = response.data;
				dispatch(orderStatistic(result));
				const updatedData = initialStatusData.map((item) => {
					const matchingItemFromAPI = result.find(
						(apiItem: any) => apiItem.statusId === item.id
					);
					return matchingItemFromAPI
						? {
								...item,
								numberOfOrders:
									matchingItemFromAPI.numberOfOrders,
						  }
						: item;
				});
				calculatePercentages(updatedData);
				setOrdersCount(updatedData);
			} catch (error) {
				console.error("Error fetching data:", error);
			}
		};

		const calculateOrderPercentages = (ordersByLocation: any) => {
			const totalOrders = ordersByLocation.reduce(
				(sum: any, location: any) => sum + location.orderCount,
				0
			);

			const updatedOrdersByLocation = ordersByLocation.map(
				(location: any) => ({
					...location,
					percentage:
						totalOrders > 0
							? (
									(location.orderCount / totalOrders) *
									100
							  ).toFixed(2)
							: 0,
				})
			);

			return updatedOrdersByLocation;
		};

		const getOrderCountByCountries = async () => {
			try {
				const response = await api.get(
					"Order/client-count-by-country",
					{
						params: { userId: clientId ? clientId : urlId },
					}
				);
				if (response.status === 200) {
					const updatedData = tableOrderByCountries.map((item) => {
						const countryData = response.data?.find(
							(data: any) => data.country === item.country
						);
						if (countryData) {
							return {
								...item,
								orderCount: countryData.orderCount,
							};
						}
						return item;
					});
					const ordersWithPercentages =
						calculateOrderPercentages(updatedData);
					setTableOrderByCountries(ordersWithPercentages);
				}
			} catch (error) {
				console.log(error);
			}
		};

		const getOrderCountByCities = async () => {
			try {
				const response = await api.get("Order/client-count-by-city", {
					params: { userId: clientId ? clientId : urlId },
				});
				const result = response.data;
				const ordersWithPercentages = calculateOrderPercentages(result);
				setOrdersCountByCity(ordersWithPercentages);

				const filteredCities = selectedCountry
					? ordersWithPercentages.filter(
							(city: any) => city.country === selectedCountry
					  )
					: ordersWithPercentages;

				const sortedCities = filteredCities
					.slice()
					.sort((a: any, b: any) => b.orderCount - a.orderCount);
				const top10Cities = sortedCities.slice(0, 10);

				if (sortedCities.length > 10) {
					const otherOrderCount = sortedCities
						.slice(10)
						.reduce(
							(total: any, city: any) => total + city.orderCount,
							0
						);
					const others = {
						city: "Others",
						orderCount: otherOrderCount,
					};
					top10Cities.push(others);
				}

				setCities(top10Cities);
			} catch (error) {
				console.error("Error fetching data:", error);
			}
		};
		setBankPaymentTableColumns([
			{
				field: "id",
				headerName: strings?.code,
				valueFormatter: (params) => codeFormatter(params.value),
				type: "numericColumn",
				filter: "agNumberColumnFilter",
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				width: 130,
				headerCheckboxSelection: true,
				checkboxSelection: true,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "statusId",
				headerName: strings?.status,
				valueFormatter: (params) => {
					const statusText = getStatusText(params.value);
					return statusText || params.value;
				},
				width: 150,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "transportPrice",
				headerName: strings?.transportPrice,
				valueFormatter: priceFormatterByCurrency("orderCurrencyId"),
				suppressSizeToFit: isSmallScreen,
			},

			{
				field: "refusalPrice",
				headerName: strings?.refusalPrice,
				valueFormatter: priceFormatterByCurrency("orderCurrencyId"),
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "exchangePrice",
				headerName: strings?.exchangePrice,
				valueFormatter: priceFormatterByCurrency("orderCurrencyId"),
				suppressSizeToFit: isSmallScreen,
			},
		]);

		setTableColumns([
			{
				// type: "numericColumn",
				// filter: "agNumberColumnFilter",
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 40,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "displayId",
				headerName: strings?.code,
				valueFormatter: (params) => codeFormatter(params.value),
				// type: "numericColumn",
				// filter: "agNumberColumnFilter",
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				width: 130,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "statusId",
				headerName: strings?.status,
				cellRenderer: (params: any) => {
					const statusColorHexCode =
						params.data.statusColorHexCode || "#000000";
					const rgbaColor = hexToRgba(statusColorHexCode, 0.08);

					return (
						<div className="statusStyle">
							<div
								className="statusValues"
								style={{
									backgroundColor: rgbaColor,
									color: statusColorHexCode,
								}}
							>
								<p className="text-lg">&#x2022;</p>
								<p className="orderStatusDescription">
									{params.data.statusDescription}
								</p>
							</div>
						</div>
					);
				},
				width: 150,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "deliveredDate",
				headerName: strings?.deliveredDate,
				width: 160,
				valueFormatter: (params) => valueDateFormatter(params?.value),
				suppressSizeToFit: isSmallScreen,
			},
			{
				// field: "buyerNameAndSurname",
				headerName: strings?.buyer,

				cellRendererFramework: (params: any) => {
					const buyerNameSurname = params.data.buyerNameAndSurname;
					const phone = params.data.buyerPhoneNo;
					return (
						<div className="buyerField">
							<p className="text-[#2C2C2C] font-semibold">
								{buyerNameSurname}
							</p>
							<p className="text-[#4F16C2] font-normal">
								{phone}
							</p>
						</div>
					);
				},
				suppressSizeToFit: isSmallScreen,
			},
			// {
			// 	field: "orderDescription",
			// 	headerName: "Pershkrimi",
			// },
			{
				field: "country",
				headerName: strings?.country,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "city",
				headerName: strings?.city,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderPrice",
				headerName: strings?.orderPrice,
				valueFormatter: priceFormatterByCurrency("orderCurrencyId"),
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "transportPrice",
				headerName: strings?.transportPrice,
				valueFormatter: priceFormatterByCurrency("orderCurrencyId"),
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "unCollectedFeeForMessage",
				headerName: "Cmimi per mesazh",
				valueFormatter: priceFormatterByCurrency("orderCurrencyId"),
				suppressSizeToFit: isSmallScreen,
			},
			// {
			// 	field: "refusalPrice",
			// 	headerName: strings?.refusalPrice,
			// 	valueFormatter: (params) => priceFormatter(params.value),
			// 	suppressSizeToFit: isSmallScreen,
			// },
			// {
			// 	field: "exchangePrice",
			// 	headerName: strings?.exchangePrice,
			// 	valueFormatter: (params) => priceFormatter(params.value),
			// 	suppressSizeToFit: isSmallScreen,
			// },
		]);
		setSettlementOrderTableColumns([
			{
				field: "displayId",
				headerName: strings?.code,
				valueFormatter: (params: any) => codeFormatter(params.value),
				// type: "numericColumn",
				// filter: "agNumberColumnFilter",
				cellStyle: (params: any) => ({
					fontSize: "15px",
					color: "blue",
				}),
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			// {
			// 	field: "sellerId",
			// 	headerName: "Seller ",
			// 	width: 160,
			// },
			{
				field: "orderPrice",
				headerName: strings?.orderPrice,
				valueFormatter: priceFormatterByCurrency("orderCurrencyId"),
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "transportPrice",
				headerName: strings?.transportPrice,
				valueFormatter: priceFormatterByCurrency("orderCurrencyId"),
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderDescription",
				headerName: strings?.orderDesc,
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "settlementStatusDescription",
				headerName: strings?.settlementStatus,
				width: 100,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "buyer",
				headerName: strings?.buyer,
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
		]);

		// The earnings-modal columns are NOT built here — they depend on `dualCurrencyEnabled`,
		// which resolves after first paint. See `earningDocumentsTableColumns` below.
		getOrderCountByCities();

		fetchSettlements(urlId);
		ClientBalance();
		getTableData(urlId);
		fetchOrdersCount();
		getOrderCountByCountries();
		fetchCashFlow();
		getClientEarningDocuments(urlId);
		getZones();
	}, [strings]);

	/**
	 * A money column and the currency it is in, side by side.
	 *
	 * Every figure on these rows is denominated by the order's own `orderCurrencyId` — one row can
	 * be LEK and the next EUR — so the currency is read per row rather than assumed. The fallback
	 * is the deployment default, which is what an order registered before the currency column
	 * existed is in.
	 */
	const orderMoneyExport = (colId: string, amountHeader?: string) =>
		moneyExportColumns({
			colId,
			// Paired with whatever the amount column is actually headed — the grid header when
			// there is one, the raw field when there is not — so the two always read as a pair.
			currencyHeaderName: `${strings?.currencyColumn} - ${
				amountHeader ?? colId
			}`,
			currency: (row: any) => row?.orderCurrencyId ?? defaultCurrencyId,
			dualCurrencyEnabled,
		});

	const tableColumnsExcel = [
		"id",
		"statusId",
		"buyerNameAndSurname",
		"orderDescription",
		"country",
		"city",
		...orderMoneyExport("orderPrice", strings?.orderPrice),
		...orderMoneyExport("transportPrice", strings?.transportPrice),
		...orderMoneyExport("refusalPrice", strings?.refusalPrice),
		...orderMoneyExport("exchangePrice", strings?.exchangePrice),
	];

	/**
	 * True for an unpaid earning row denominated in EUR.
	 *
	 * ⚠️ The `dualCurrencyEnabled` half is not decoration. **EUR is Kosovo's own currency**, so
	 * every PostmanKS earning row legitimately carries `currencyId = 1`; a bare id test would read
	 * the whole Kosovo ledger as a foreign currency. Same reasoning as `isEurRow` below, and the
	 * same reason the stored procedure pairs the test with `@IsDual`.
	 */
	const isEurEarning = (row: any) =>
		dualCurrencyEnabled && row?.currencyId === CURRENCY_EUR;

	/**
	 * The client's unpaid refusal / exchange charges, **bucketed by the currency each row is
	 * denominated in**.
	 *
	 * Two totals, never one. `1.50 €` and `250.00 L` are not `251.50` of anything, and summing them
	 * into the LEK settlement figure is precisely the defect this page shipped: the modal showed a
	 * single *Vlera e Barazimit* with three mixed-currency rows subtracted from it. Each total is
	 * deducted from the settlement half of its own currency, mirroring `CreateSettlement_sp`, which
	 * buckets the same earnings on `EarningsDocuments.CurrencyId` server-side.
	 *
	 * Reads the API's own split (`value` / `valueEur`) rather than re-deriving it from `currencyId`,
	 * so the screen and the database agree by construction. Off PostmanAL — and against an
	 * API/database that predates the split — `valueEur` is absent, `eur` stays 0 and `local` is the
	 * same total this page has always computed.
	 */
	const earningTotals = useMemo(
		() => sumEarningTotals(earningDocumentsData),
		[earningDocumentsData]
	);

	/**
	 * Total → refusals → to pay for the earnings modal, one column per currency. Opening the modal
	 * has already taken the refusals off `settlementSum` / `settlementSumEur`, so those are the
	 * amounts to pay and the total is them plus what came off.
	 */
	const settlementBreakdown = useMemo<SettlementBreakdownColumn[]>(() => {
		const deducted = earningsIncluded
			? earningTotals
			: { local: 0, eur: 0 };
		const column = (
			net: number | string | null | undefined,
			refusals: number,
			currencyId: number | null | undefined
		): SettlementBreakdownColumn => ({
			currencyId,
			total: round2(Number(net ?? 0) + refusals),
			refusals,
			toPay: round2(Number(net ?? 0)),
		});

		const local = column(settlementSum, deducted.local, defaultCurrencyId);
		return dualCurrencyEnabled
			? [local, column(settlementSumEur, deducted.eur, CURRENCY_EUR)]
			: [local];
	}, [
		earningsIncluded,
		earningTotals,
		settlementSum,
		settlementSumEur,
		dualCurrencyEnabled,
		defaultCurrencyId,
	]);

	/**
	 * The earnings-modal grid — one unpaid refusal or exchange charge per row.
	 *
	 * A `useMemo` rather than a `setState` inside the page's mount effect, because
	 * `dualCurrencyEnabled` resolves **after** first paint: columns built once on mount would keep
	 * the single-currency layout forever, which is the trap `04-COMPONENTS.md` warns about for
	 * every `dualMoneyColumns` caller.
	 */
	const earningDocumentsTableColumns = useMemo<ColDef[]>(
		() => [
			{
				field: "id",
				headerName: strings?.id,
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
			// One column per currency. A row is denominated in exactly one of them, so the other
			// renders `—` — never `0.00`, and never a merged `1.50L · 250.00€` cell.
			//
			// The gate normally lives inside `dualMoneyColumns` (W-D17) and callers do not write
			// it. It is written here for one reason only: this ticket must leave PostmanKS and
			// PostmanMK pixel-identical, and the pair's off-state formats through
			// `formatDualMoneyAmount` (`250.00 €`, `—` for nothing) rather than through
			// `priceFormatter` (`250.00€`, `-`). Delete the branch when this grid is migrated to
			// house money formatting along with the rest of the page.
			...(dualCurrencyEnabled
				? dualMoneyColumns({
						field: "value",
						headerName: strings?.value,
						dualCurrencyEnabled,
						defaultCurrencyId,
						width: 160,
						suppressSizeToFit: isSmallScreen,
				  })
				: [
						{
							field: "value",
							headerName: strings?.value,
							valueFormatter: (params: any) =>
								priceFormatter(params?.value),
							width: 160,
							suppressSizeToFit: isSmallScreen,
						},
				  ]),
			{
				field: "earningReason",
				headerName: strings?.earningReason,
				width: 350,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "createdAt",
				headerName: strings?.createdAt,
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
		],
		[strings, dualCurrencyEnabled, defaultCurrencyId, isSmallScreen]
	);

	/**
	 * The balance cards — **one card per currency**, each holding every metric for that currency.
	 *
	 * Built as data rather than JSX because the card count depends on the deployment: two on
	 * PostmanAL, one everywhere else. There is exactly one card per entry in `slices`, so an
	 * empty or unlabelled card cannot appear — a currency either has a card or it does not.
	 */
	const balanceCards = useMemo(() => {
		const slices = [
			{
				currencyId: defaultCurrencyId,
				balance: currentBalance,
				sales: orderSales,
				refusals: orderRefusal,
				turnover: cashFlow,
				transport: transportPayment,
			},
			...(dualCurrencyEnabled
				? [
						{
							currencyId: CURRENCY_EUR,
							balance: currentBalanceEur,
							sales: orderSalesEur,
							refusals: orderRefusalEur,
							turnover: cashFlowEur,
							transport: transportPaymentEur,
						},
				  ]
				: []),
		];

		return slices.map((slice) => ({
			currencyId: slice.currencyId,
			sections: [
				{
					title: strings?.obligation,
					value: slice.balance,
					lines: [
						{ value: slice.sales, label: strings?.orderSales },
						{
							value: slice.refusals,
							label: strings?.orderRefusals,
						},
					],
				},
				{
					title: strings?.totalTurnover,
					value: slice.turnover,
					lines: [
						{
							value: slice.transport,
							label: strings?.paidForTransport,
						},
						{
							value: slice.refusals,
							label: strings?.orderRefusals,
						},
					],
				},
			],
		}));
	}, [
		dualCurrencyEnabled,
		defaultCurrencyId,
		currentBalance,
		currentBalanceEur,
		orderSales,
		orderSalesEur,
		orderRefusal,
		orderRefusalEur,
		cashFlow,
		cashFlowEur,
		transportPayment,
		transportPaymentEur,
		strings?.obligation,
		strings?.totalTurnover,
		strings?.orderSales,
		strings?.orderRefusals,
		strings?.paidForTransport,
	]);

	/**
	 * The settlements grid sizes itself to the panel rather than to a set of pixel widths.
	 *
	 * Every column used to carry a fixed `width`, and they summed to less than the panel is wide —
	 * so the table ended part-way across with dead space beside it, while `Nr. i porosive` was
	 * wrapping across three clipped header lines inside its 70px. `flex` shares out the actual
	 * width instead and follows the panel when it resizes; `minWidth` is the point below which a
	 * column stops giving ground and the grid scrolls instead. The QR column keeps a fixed width —
	 * it holds one icon and has no use for a share of anything.
	 *
	 * The ratios favour the money columns, which have to fit a seven-digit LEK figure, and the
	 * status column, which holds a pill or a button rather than a word.
	 */
	const columnDefs2 = [
		{
			headerName: strings?.code,
			valueFormatter: (params: any) => codeFormatter(params.value),
			field: "id",
			cellRenderer: (params: any) => {
				const date = valueDateFormatter(params.data.createdAt);
				return (
					<div>
						<p className="text-[15px] text-[#4F16C2] font-semibold	">
							{params.data.id}
						</p>
						<p className="font-inter font-normal font-medium text-sm leading-6 text-gray-400">
							{date}
						</p>
					</div>
				);
			},
			flex: 1.4,
			minWidth: 150,
		},
		// One column per currency. A settlement can span both, paid out as two separate
		// transactions, so "Vlera LEK" and "Vlera EUR" are independent amounts — never a total.
		...dualMoneyColumns({
			field: "sumValue",
			headerName: strings?.value,
			dualCurrencyEnabled,
			defaultCurrencyId,
			flex: 1.2,
			minWidth: 130,
		}),
		{
			headerName: strings?.numberOfOrders,
			field: "numberOfOrders",
			// Wide enough for "Nr. i porosive" to sit on one line. At 70px it broke across three
			// and the last was clipped, and there is no fixing that with `wrapHeaderText` /
			// `autoHeaderHeight` — both arrived in AG-Grid 28 and this is 27.3, so the
			// `wrapHeaderText` in `defaultColDef` does nothing either.
			flex: 1,
			minWidth: 140,
			cellRenderer: (params: any) => {
				return <p className="font-semibold">{params.value}</p>;
			},
		},
		{
			headerName: strings?.status,
			field: "settlementStatusId",
			flex: 1.4,
			minWidth: 170,
			cellRenderer: (params: any) => {
				const statusColorHexCode =
					params.data.statusColorHexCode || "#ffff";
				const rgbaColor = hexToRgba(statusColorHexCode, 0.08);
				if (params?.value === 4) {
					return (
						<Button
							size="large"
							style={{ color: "green" }}
							icon={<CheckCircleOutlined />}
						>
							{strings?.submitSettlement}
						</Button>
					);
				} else if (params?.value === 1) {
					return (
						<Button disabled icon={<CheckCircleOutlined />}>
							{params?.data.settlementStatusDescription}
						</Button>
					);
				} else {
					return (
						<div className="statusStyle">
							<div
								className="statusValues"
								style={{
									backgroundColor: statusColorHexCode,
									color: "white",
								}}
							>
								<p className="text-lg">&#x2022;</p>
								<p className="w-full">
									{params.data.settlementStatusDescription}
								</p>
							</div>
						</div>
					);
				}
			},
		},
		{
			headerName: strings?.qr,
			cellRenderer: () => <QrCodeIcon onClick={settlementDetailsPdf} />,
			// No flex: one icon, so it takes its width and leaves the rest to the columns that
			// hold text.
			width: 90,
			minWidth: 80,
			maxWidth: 120,
		},
	];

	const settlementDetailsColumns = [
		{ field: "orderId", headerName: "ID e Porosisë" },
		{ field: "sellerId", headerName: "ID e Shitësit" },
		{
			field: "orderPrice",
			headerName: "Çmimi i Porosisë",
			// Was a hardcoded "€", which printed euro signs on LEK amounts on PostmanAL.
			valueGetter: (params: any) =>
				formatMoney(
					params.data.orderPrice,
					params.data.orderCurrencyId
				),
		},
		{
			field: "transportPrice",
			headerName: "Çmimi i Transportit",
			valueGetter: (params: any) =>
				formatMoney(
					params.data.transportPrice,
					params.data.orderCurrencyId
				),
		},
		{
			field: "packageDescription",
			headerName: "Përshkrimi i Porosisë",
			valueGetter: (params: any) => params.data.orderDescription ?? "N/A",
		},
		{ field: "statusId", headerName: "ID e Statusit" },
		{
			field: "description",
			headerName: "Përshkrimi i Statusit të Porosisë",
		},
		{ field: "settlementId", headerName: "ID e Barazimit" },
		{
			field: "settlementStatusId",
			headerName: "ID e Statusit të Barazimit",
		},
		{
			field: "settlementStatusDescription",
			headerName: "Përshkrimi i Statusit të Barazimit",
		},
		{ field: "buyer", headerName: "Blerësi" },
		{ field: "client", headerName: "Shitësi" },
		{ field: "uniqueSellerIdentifier", headerName: "Kodi unik i shitësit" },
		{ field: "driverName", headerName: "Përgjegjësi" },
	];

	const getRowStyle = (params: any) => {
		if (params.node.rowIndex % 2 === 0) {
			return { backgroundColor: "#F9F8FC" };
		}
		return undefined;
	};

	const selectedColumnDefs2 = [
		"id",
		"sumValue",
		"createdAt",
		"settlementStatusId",
	];
	const selectedSettlementDetailsColumns = [
		"settlementId",
		"settlementStatusDescription",
		"orderId",
		"description",
		"uniqueSellerIdentifier",
		"client",
		"buyer",
		...orderMoneyExport("orderPrice", strings?.orderPrice),
		...orderMoneyExport("transportPrice", strings?.transportPrice),
		"packageDescription",
		"driverName",
	];

	const selectedSettlementOrderColumn = [
		"orderId",
		...orderMoneyExport("orderPrice", strings?.orderPrice),
		...orderMoneyExport("transportPrice", strings?.transportPrice),
		"orderDescription",
		"settlementStatusDescription",
		"buyer",
	];

	const selectedEarningDocumentColumn = [
		"id",
		// An earnings document carries its own `currencyId` — a refusal fee on a EUR order is
		// booked in EUR — so this is not the order currency and must not be read as one.
		...moneyExportColumns({
			colId: "value",
			currencyHeaderName: strings?.currencyColumn,
			currency: (row: any) => row?.currencyId ?? defaultCurrencyId,
			// The row's figure lives in the bucket its own currency names: a EUR row reports
			// `value = 0` and carries its amount in `valueEur`. Without this the sheet would
			// export `0` under a `EURO` heading. Off PostmanAL `isEurEarning` is always false, so
			// this resolves to the plain `value` the sheet has always exported.
			amount: (row: any) =>
				isEurEarning(row) ? row?.valueEur : row?.value,
			dualCurrencyEnabled,
		}),
		"earningReason",
		"createdAt",
	];

	const getStatusText = (statusId: number) => {
		switch (statusId) {
			case 1:
				return "Te reja";
			case 5:
				return "Ne transport";
			case 2:
				return "Në pritje të marrjes";
			case 4:
				return "Ne depo";
			case 6:
				return "Dorezuar";
			case 7:
				return "Refuzuar";
			case 8:
				return "Ne pritje";
			case 10:
				return "Kthyer";

			default:
				return "Unknown Status";
		}
	};

	const [page, setPage] = useState(1);
	const pageSize = 20;
	const onPageChange = (newPage: any) => {
		setPage(newPage);
	};
	const displayedData = tableData.slice(
		(page - 1) * pageSize,
		page * pageSize
	);
	const handlePageSizeChange = (current: number, size: number) => {
		setPage(size);
	};

	const settlementStartIndex = (page - 1) * pageSize + 1;
	const settlementEndIndex = Math.min(
		page * pageSize,
		settlementData?.length
	);

	const deliveredOrders = tableData.filter(
		(order: any) => order.statusId === orderStatuses.DELIVERED
	)?.length;
	const refusedOrders = tableData.filter(
		(order: any) => order.statusId === orderStatuses.REJECTED
	)?.length;
	const totalOrders = tableData?.length;
	const deliveredPercentage = (deliveredOrders / totalOrders) * 100;
	const refusedPercentage = (refusedOrders / totalOrders) * 100;

	const twoColors = {
		[deliveredPercentage + "%"]: "yellow",
		[refusedPercentage + "%"]: "blue",
	};

	const settlementDetailsPdf = async () => {
		const ids = (settlementGridRef.current as any)?.api
			?.getSelectedRows()
			?.map((item: any) => ({
				id: item.id,
			}));

		if (!ids?.length) {
			toast.warning("Selekto porosite per te printuar");
			return true;
		}
		const data = {
			settlementIds: ids,
			isRefusal: false,
		};
		try {
			const response = await api.post(
				"Settlement/print-settlement",
				data,
				{
					responseType: "blob",
				}
			);
			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				console.error(
					"Popup blocked. Please allow popups for this site."
				);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const getSettlementOrders = async (settlementId: string) => {
		try {
			const response = await api.get(
				"Settlement/select-settlement-orders",
				{ params: { settlementId: settlementId } }
			);
			if (response.status === 200) {
				setSettlementOrderData(response?.data);
			}
		} catch (error) {
			console.log("Error:" + error);
		}
	};

	const getClientEarningDocuments = async (clientId: string | null) => {
		try {
			const response = await api.get(
				"Earnings/select-client-earning-documents",
				{ params: { clientId: clientId } }
			);
			if (response.status === 200) {
				setEarningDocumentsData(response?.data);
				return response?.data as any[];
			}
		} catch (error) {
			console.log("Error:" + error);
		}
		return undefined;
	};

	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "displayId":
				await onOrderIdClicked(params?.data);
				break;
		}
	};
	const onSettlementGridClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "settlementStatusId":
				await onSettlementStatusClicked(params?.data);
				break;
			case "id":
				await onDetailsClicked(params?.data);
				break;
		}
	};

	const onSettlementStatusClicked = async (data: any) => {
		if (data?.settlementStatusId === settlementStatuses.WITHDRAWN) {
			// const cashOut = data?.sumValue;
			const selectedRowSettlementId = data?.id;
			// setCashOut(cashOut);
			setSettlementId(selectedRowSettlementId);
			setOpenSettlementClientWithdrawnModal(true);
		}
	};

	const onDetailsClicked = async (data: any) => {
		if (data?.settlementStatusId !== settlementStatuses.REQUESTED) {
			await getSettlementOrders(data?.id);
			setOpenSettlementOrderDetailsModal(true);
			setSettlementDetailsId(data?.id);
		}
	};

	const onOrderIdClicked = (data: any) => {
		const orderId = data?.id?.toString();
		// navigate("/order-details", { state: orderId });
		const url = `/order-details?id=${orderId}`;
		window.open(url, "_blank");
	};

	/**
	 * Takes the client's unpaid refusal/exchange debt off the settlement value — always, as soon as
	 * the earnings modal opens. It used to be an opt-in checkbox; since 2026-09-26 these are never
	 * left out of a settlement, so there is nothing for staff to choose.
	 *
	 * **Each currency's debt comes off the settlement half of its own currency** — LEK refusals
	 * reduce the LEK figure, EUR refusals the EUR one — which is what `CreateSettlement_sp` does
	 * server-side with the very same earning ids.
	 *
	 * Computed from the rows just fetched, not from `earningTotals`, and with functional updates:
	 * it runs inside `handleFirstStepOfProccessing`, a memoised callback whose captured state can be
	 * a render behind. `Number(...)` because `onRowSelected` stores the sums as `.toFixed(2)`
	 * strings.
	 */
	const includeEarningsInSettlement = useCallback((rows: any[]) => {
		const totals = sumEarningTotals(rows);
		setSettlementSum((sum) => round2(Number(sum ?? 0) - totals.local));
		setSettlementSumEur((sum) => round2(Number(sum ?? 0) - totals.eur));
		setEarningsIncluded(true);
	}, []);

	const handleFirstStepOfProccessing = useCallback(async () => {
		if (!cashRegisterId || cashRegisterId === 0) {
			toast.error("Nuk keni te zgjedhur nje Cash Register");
			return;
		}
		if (settlementSum === 0 && selectedSettlementOrders.length === 0) {
			console.log("firstfirstfirst");
			return;
		}
		const processingValidation =
			await fetchSettlementDisableRequestValidation(
				documentTypes.SETTLEMENT,
				clientId
			);
		const validationInfo = await fetchSettlementValidationForInfo(
			documentTypes.SETTLEMENT,
			clientId
		);

		setDisabledSettlementProcessing(processingValidation);
		if (
			!settlementId &&
			processingValidation &&
			validationInfo?.currentOpenReturnOrders === false
		) {
			toast.error("Nuk mund te procesoni barazim per kete klient");
			return;
			// } else if (
			// 	processingValidation &&
			// 	validationInfo?.currentOpenReturnOrders === true
			// ) {
			// 	toast.error(
			// 		"Barazimi nuk mund te procesohet. Klienti ka kthime te porosive te cilat nuk jane te dorezuara!"
			// 	);
			// 	return;
		} else if (settlementId || !processingValidation) {
			// Always re-read, whatever the page loaded: a refusal can become due while the page is
			// open, and every due one has to be deducted.
			const earnings = await getClientEarningDocuments(clientId);
			if (!earnings) {
				// Could not load them. Never process without them.
				return;
			}
			if (earnings.length === 0) {
				setOpenSettlementProcessingModal(true);
				return;
			}
			includeEarningsInSettlement(earnings);
			setOpenEarningDocumentsModal(true);
		}
	}, [cashRegisterId, clientId, settlementId, includeEarningsInSettlement]);

	const initialValue = 0;
	const bankInitialValue = 0;

	const contributionForRow = (item: any) =>
		isBankPayment
			? item.orderPrice
			: item.orderPrice -
			  item.transportPrice -
			  (storagePayment ? 2 : 0) -
			  (requiresDeliveryCode ? item.unCollectedFeeForMessage : 0);

	let exemptClientIds = [1, 2, 3];

	/** True for an order registered in EUR. Never for a single-currency deployment. */
	const isEurRow = (row: any) =>
		dualCurrencyEnabled &&
		(row?.orderCurrencyId ?? row?.currencyId) === CURRENCY_EUR;

	const onRowSelected = useCallback(
		(event: any) => {
			const api = event.api;

			const selected = api.getSelectedRows();

			/*
			 * Two running sums, not one.
			 *
			 * A settlement is partitioned by the currency each order was REGISTERED in and paid
			 * out as two separate cash-register transactions. Adding a LEK 5,000 order to a EUR 50
			 * one gives 5,050, a number that means nothing — and validating that against the LEK
			 * balance alone let a EUR selection through a ceiling roughly 96x too generous.
			 *
			 * Each sum is therefore capped by its OWN balance, and the toast says which currency
			 * was exceeded. Off PostmanAL isEurRow() is always false, the EUR sum stays 0, and
			 * this behaves exactly as it did before.
			 */
			const sumFor = (predicate: (r: any) => boolean) =>
				selected
					.filter(predicate)
					.reduce(
						(acc: number, r: any) => acc + contributionForRow(r),
						0
					);

			const rawSettlementSum =
				(initialValue ?? 0) + sumFor((r: any) => !isEurRow(r));
			const rawSettlementSumEur = sumFor(isEurRow);

			const display = rawSettlementSum.toFixed(2);
			const displayEur = rawSettlementSumEur.toFixed(2);

			const exempt = exemptClientIds.includes(Number(clientId));

			// Compare as numbers. The old code compared a .toFixed() string against a number and
			// only worked because JS coerced it — "9.50" > 100 is a lexicographic trap.
			if (
				!exempt &&
				event.node.selected &&
				rawSettlementSum > Number(currentBalance ?? 0)
			) {
				event.node.setSelected(false, true);
				toast.error(
					`Shuma e barazimit (${currencySymbol()}) nuk mund të kalojë bilancin aktual.`
				);
				return;
			}

			if (
				!exempt &&
				event.node.selected &&
				rawSettlementSumEur > Number(currentBalanceEur ?? 0)
			) {
				event.node.setSelected(false, true);
				toast.error(
					`Shuma e barazimit (${currencySymbol(
						CURRENCY_EUR
					)}) nuk mund të kalojë bilancin aktual në ${currencySymbol(
						CURRENCY_EUR
					)}.`
				);
				return;
			}

			setSettlementSum(display);
			setSettlementSumEur(displayEur);

			if (isBankPayment) {
				const rawTransport =
					(bankInitialValue ?? 0) +
					selected.reduce(
						(acc: number, r: any) =>
							acc +
							(r.transportPrice +
								(storagePayment ? 2 : 0) +
								(requiresDeliveryCode
									? r.unCollectedFeeForMessage
									: 0)),
						0
					);
				setTransportSum(rawTransport);
			}
		},
		[
			isBankPayment,
			storagePayment,
			initialValue,
			bankInitialValue,
			requiresDeliveryCode,
			uncollectedValueForDeliveryCodeFee,
			currentBalance,
			currentBalanceEur,
			dualCurrencyEnabled,
			setSettlementSum,
			setTransportSum,
		]
	);

	const [isProcessing, setIsProcessing] = useState(false);
	// The state above drives the spinner; this ref is the actual lock. Two clicks landing before
	// React re-renders both read `isProcessing === false`, so the state alone lets a second
	// settlement request through.
	const settlementRequestInFlight = useRef(false);

	const settlementProcessing1 = async () => {
		if (settlementRequestInFlight.current) {
			return;
		}
		settlementRequestInFlight.current = true;
		setIsProcessing(true);
		try {
			const payload = {
				sellerId: clientId,
				orderIds: selectedSettlementOrders.map((id: number) => ({
					id,
				})),
				earningIds:
					earningsIncluded && earningDocumentsData.length > 0
						? earningDocumentsData.map((x: any) => ({
								id: x.id,
						  }))
						: [],
				cashRegisterId: cashRegisterId,
				// Numbers, never null: the API binds both to non-nullable decimals, and a null (the
				// state after a previous settlement on this page) fails the whole request with 400.
				settlementSum: Number(settlementSum ?? 0),
				transportSum: Number(transportSum ?? 0),
			};
			await api.post(
				"Settlement/settlement-processing-for-clients",
				payload
			);
			toast.success("Barazimi u procesua me sukses!");
		} catch (error) {
			// The axios interceptor has already toasted the API's message.
			console.log(error);
		} finally {
			setOpenSettlementProcessingModal(false);
			setOpenEarningDocumentsModal(false);
			setEarningsIncluded(false);
			setSettlementId(null);
			setSettlementSum(0);
			setSettlementSumEur(0);
			setTransportSum(null);
			// Held until the page has reloaded, so nothing can be resubmitted against the
			// orders and earnings that were just settled.
			try {
				await getTableData(clientId);
				await fetchSettlements(clientId);
				await getClientEarningDocuments(clientId);
			} finally {
				settlementRequestInFlight.current = false;
				setIsProcessing(false);
			}
		}
	};

	//Procesimi i barazimit, krijohet edhe transaction meniher
	const settlementProcessing = async () => {
		try {
			setIsProcessing(true);
			let response: AxiosResponse<any, any> | undefined = undefined;
			let createdSettlementId: any | null = undefined;
			//nese nuk eshte i ndalum procesimi pa kerkese, edhe nese nuk ka kerkese
			if (!disabledSettlementProcessing && !settlementId) {
				response = await createSettlementProcessingWithoutRequest();
				createdSettlementId = response?.data;
				setSettlementId(createdSettlementId);
			} else {
				response = await createSettlementProcesingWithRequest();
			}
			if (response?.status !== 200) {
				return;
			}
			createSettlementTransaction(createdSettlementId);
			if (isBankPayment) {
				await insertEarning(createdSettlementId);
			}
			if (earningsIncluded && earningDocumentsData?.length > 0) {
				const settlementIdToUseForEarning = !settlementId
					? createdSettlementId
					: settlementId;
				await validateEarningsDocuments(settlementIdToUseForEarning);
			}
			if (response && response.status === 200) {
				toast.success("Success!");
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsProcessing(false);
			setOpenSettlementProcessingModal(false);
			setOpenEarningDocumentsModal(false);
			setSettlementId(null);
			setSettlementSum(0);
			setSettlementSumEur(0);
			setTransportSum(null);
			await getTableData(clientId);
			await fetchSettlements(clientId);
		}
	};

	const createSettlementProcesingWithRequest = async () => {
		try {
			const response = await api.post(
				"Settlement/settlement-processing",
				{
					settlementStatusUpdate: {
						settlementId: settlementId,
					},
					orderInsert: settlementOrderPayments,
				}
			);
			return response;
		} catch (error) {
			console.log(error);
		}
	};

	const createSettlementProcessingWithoutRequest = async () => {
		const settlementRefusal = {
			applicantId: clientId,
			orderId: selectedSettlementOrders,
		};
		try {
			const response = await api.post(
				"Settlement/create-settlement-processing-for-client",
				settlementRefusal
			);
			return response;
		} catch (error: any) {
			console.log("Error: " + error.message);
			toast.error("Error: " + error.message);
		}
	};

	const [insertEarningDisabled, setInsertEarningDisabled] = useState(false);

	const insertEarning = async (id: any | null) => {
		const data = {
			value: transportSum,
			settlementId: settlementId ? settlementId : id,
		};
		setInsertEarningDisabled(true);
		try {
			const response = await api.post("Earnings/register-earnings", data);
			if (response.status === 200) {
				toast.success("Success!");
				setBankTransportPaymentModal(false);
				setTransportSum(0);
				await getTableData(clientUrlId);
				await fetchSettlements(clientUrlId);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setInsertEarningDisabled(false);
		}
	};

	const createSettlementTransaction = async (id: any | null) => {
		const data = {
			cashOut: settlementSum,
			cashRegisterId: cashRegisterId,
		};
		try {
			const response = await api.post(
				"CashRegisterTransaction/insert-cash-register-from-settlement",
				{
					cashRegisterTransaction: data,
					settlementOrderPayments: {
						settlementId: settlementId ? settlementId : id,
						settlementValue: settlementSum,
					},
				}
			);
			if (response.status === 200) {
				setOpenSettlementClientWithdrawnModal(false);
				// toast.success("Successful!");
				setSettlementId(null);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const [selectedOrdersForProcessing, setSelectedOrdersForProcessing] =
		useState<number>();

	const onSelectionChange = (event: any) => {
		const selectedRows = (gridRef.current as any).api.getSelectedRows();
		const selectedOrderIds = selectedRows.map((item: any) => item?.id);
		const refactoredSelectedRows = selectedRows.map((item: any) => ({
			settlementId: settlementId,
			orderId: item.id,
		}));
		setSelectedSettlementOrders(selectedOrderIds);
		setSettlementOrderPayments(refactoredSelectedRows);
		// vleraBarazimit(event);
	};

	const filteredOrders = ordersCount.filter(
		(order) =>
			order.id === orderStatuses.DELIVERED ||
			order.id === orderStatuses.REJECTED
	);

	const colorMapping = {
		Delivered: "#219653",
		Rejected: "#FF0000",
	};

	const cancelModal = () => {
		setOpenSettlementOrderDetailsModal(false);
		setSettlementOrderData(null);
		setSettlementDetailsId(null);
	};

	const dataChart = filteredOrders.map((order, index) => ({
		id: index,
		value: order.numberOfOrders,
		color: colorMapping[order.description as keyof typeof colorMapping],
	}));

	const validateClientDetails = (data: any) => {
		if (!data.firstName || data.firstName.trim().length === 0) {
			toast.error("Emri është i detyrueshëm");
			return false;
		}
		if (!data.lastName || data.lastName.trim().length === 0) {
			toast.error("Mbiemri është i detyrueshëm");
			return false;
		}
		if (!data.phone || data.phone.trim().length === 0) {
			toast.error("Numri i telefonit është i detyrueshëm");
			return false;
		}
		if (!data.address || data.address.trim().length === 0) {
			toast.error("Adresa është e detyrueshme");
			return false;
		}
		if (!data.username || data.username.trim().length === 0) {
			toast.error("Emërtimi i përdoruesit është i detyrueshëm");
			return false;
		}
		if (
			clientDetails?.businessName?.length > 0 &&
			(!data.businessName || data.businessName.trim().length <= 0)
		) {
			toast.error("Emri i biznesit eshte i detyrueshem");
			return false;
		}
		if (
			clientDetails?.businessName?.length > 0 &&
			(!data.businessNumber || data.businessNumber.trim().length === 0)
		) {
			toast.error("Numri i biznesit eshte i detyrueshem");
			return false;
		}
		return true;
	};

	const saveClientChanges = async () => {
		if (password !== confirmPassword) {
			toast.error("Fjalëkalimet nuk janë të njejtë!");
			return;
		}
		if (password !== "" && !validatePassword(password)) {
			toast.error(
				"Fjalëkalimi duhet të jetë së paku 8 karaktere dhe të përfshijë një shkronjë të madhe dhe një numër."
			);
			return;
		}
		if (!identificationNumber) {
			toast.error("Nr i leternjoftimit është i detyrueshëm.");
			return false;
		}
		if (identificationNumber.length !== parseInt(personalIdLength)) {
			toast.error(
				`Nr i leternjoftimi duhet te kete ${personalIdLength} karaktere`
			);
			return false;
		}

		const data = {
			sellerId: clientId,
			firstName: name,
			lastName: lastName,
			email: email,
			phone: phone,
			username: username,
			address: address,
			sectionId: sectionId,
			latitude: apiLatitude?.toString() ?? null,
			longitude: apiLongitude?.toString() ?? null,
			businessName: businessName,
			businessNr: businessPhoneNumber,
			businessNumber: businessNumber,
			password: password ?? null,
			bankPackageTransportPayment: bank,
			cashPackageTransportPayment: cash,
			identificationNumber: identificationNumber,
			collectRegionDescription: collectRegionDescription,
			requiresDeliveryCode: requiresDeliveryCode,
			deliveryCodeFee: requiresDeliveryCode ? deliveryCodeFee : null,
		};

		if (validateClientDetails(data)) {
			try {
				const response = await api.post(
					"User/update-existing-client",
					data
				);
				if (response.status === 200) {
					setEditable(false);
					await fetchData(clientId);
					await selectClientDeliveryCodeCountAndValue();
					window.scrollTo({ top: 0, behavior: "smooth" });
					toast.success("Të dhënat u përditësuan me sukses!");
				}
			} catch (error) {
				console.log("Error: ", error);
			}
		}
	};

	const confirmBlocking = async () => {
		try {
			const response = await api.post(
				"User/block-client-accounts",
				null,
				{
					params: { sellerId: clientId },
				}
			);
			if (response.status === 200) {
				setOpenBlockingModal(false);
				toast.success("Llogari u bllokua me sukses");
				navigate("/clients");
			}
		} catch (error) {
			console.log(error);
		}
	};

	const [
		deliverSettlementToClientDisabled,
		setdeliverSettlementToClientDisabled,
	] = useState(false);

	const deliverSettlementToClient = async () => {
		const data = {
			settlementStatusId: 7,
			settlementId: settlementId,
		};
		setdeliverSettlementToClientDisabled(true);
		try {
			const response = await api.post(
				"Settlement/settlement-status-update",
				data
			);
			if (response.status === 200) {
				toast.success("Porosia u dorezua tek klienti me sukses!");
				await getTableData(clientUrlId);
				await fetchSettlements();
				setOpenSettlementClientWithdrawnModal(false);
			}
		} catch (error: any) {
			toast.error("Error: " + error.message);
		} finally {
			setdeliverSettlementToClientDisabled(false);
		}
	};

	const closeEarningDocumentsModal = () => {
		setOpenEarningDocumentsModal(false);
		// Cancelled: give back what opening the modal deducted, in each currency separately.
		if (earningsIncluded) {
			setSettlementSum((sum) =>
				round2(Number(sum ?? 0) + earningTotals.local)
			);
			setSettlementSumEur((sum) =>
				round2(Number(sum ?? 0) + earningTotals.eur)
			);
		}
		setEarningsIncluded(false);
	};

	const validateEarningsDocuments = async (settlementId: any) => {
		const earningDocumentIds = earningDocumentsData.map((item: any) => ({
			earningId: item.id,
			settlementId: settlementId,
			cashRegisterId: cashRegisterId,
			settlementSum: settlementSum,
			transportSum: transportSum,
		}));
		try {
			const response = await api.post(
				"Earnings/update-earning-with-cash-for-settlement",
				earningDocumentIds
			);
			if (response.status === 200) {
				toast.success(
					"Borxhi per porosite e nderruara dhe refuzuara per klientin u validuan "
				);
				closeEarningDocumentsModal();
			}
		} catch (error) {
			console.log(error);
		}
	};

	const onExportSelectedSettlement = () => {
		exportDataToCsv(
			settlementDetailsGridRef,
			selectedSettlementDetailsColumns
		);
	};
	const onExportSelectedTableColumns = () => {
		exportDataToCsv(gridRef, tableColumnsExcel);
	};
	const onExportSelectedSettlementOrder = () => {
		exportDataToCsv(settlementOrderGridRef, selectedSettlementOrderColumn);
	};
	const onExportSelectedEarningDocuments = () => {
		exportDataToCsv(earningDocumentsGridRef, selectedEarningDocumentColumn);
	};

	const changeSwitch = () => {
		setCash((prevState) => !prevState);
		setBank((prevState) => !prevState);
	};

	const calculatePercentages = (updatedData: any) => {
		const filteredChartOrders = updatedData?.filter(
			(order: any) =>
				order.id === orderStatuses.DELIVERED ||
				order.id === orderStatuses.REJECTED
		);
		const totalOrders = filteredChartOrders?.reduce(
			(total: any, status: any) => total + status?.numberOfOrders,
			0
		);
		const calculatedPercentages = filteredChartOrders?.map(
			(status: any) => ({
				...status,
				percentage:
					totalOrders === 0
						? 0
						: (
								(status?.numberOfOrders / totalOrders) *
								100
						  ).toFixed(2),
			})
		);
		setPercentages(calculatedPercentages);
	};

	const fetchGeoData = useCallback(async (latitude: any, longitude: any) => {
		try {
			const response = await api.get("External/get-geo-data", {
				params: {
					lng: longitude,
					lat: latitude,
					category: CONFIG?.zoneMbledhs ?? "",
				},
			});
			if (response.status === 200) {
				const result = response?.data;
				const section = Number(result.geofences[0]?.name);
				setSectionId(section);
				// setFinalAddress(result.result);
			}
		} catch (error) {
			console.log(error);
		}
	}, []);

	const handleChange = (address: any) => {
		const selectedLocation = address;
		if (selectedLocation) {
			const lat = selectedLocation.lat;
			const lng = selectedLocation.lng;
			setApiLatitude(lat);
			setApiLongitude(lng);
			fetchGeoData(lat, lng);
			setAddress(selectedLocation.label ?? "");
		}
	};

	const changeSwichForDeliverCode = () => {
		setRequiresDeliveryCode((prevState) => !prevState);
		setDeliveryCodeFee(0);
	};

	const changeDeliverCodeFee = (text: any) => {
		const cleaned = text.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

		if (cleaned === "") {
			setDeliveryCodeFee(0);
			return;
		}

		const value = Number(cleaned);
		if (!isNaN(value) && value >= 0) {
			setDeliveryCodeFee(cleaned);
		}
	};

	return (
		<>
			<div className={classes.mainContainer}>
				<OrderStatus />
				<div className={classes.heroContainer}>
					<div className={classes.firstContainer}>
						<div className={classes.clientInfo}>
							<OrderDetailRow
								label={strings?.id}
								value={clientId}
								className={classes.detailRow}
								style={style.oddRow}
							/>
							<OrderDetailRow
								label={strings?.name}
								value={name}
								className={classes.detailRow}
								editable={editable}
								onChangeText={setName}
							/>
							<OrderDetailRow
								label={strings?.lastName}
								value={lastName}
								className={classes.detailRow}
								editable={editable}
								onChangeText={setLastName}
								style={style.oddRow}
							/>
							<OrderDetailRow
								label={strings?.emailAddress}
								value={email}
								className={classes.detailRow}
								editable={editable}
								onChangeText={setEmail}
							/>
							<OrderDetailRow
								label={strings?.username}
								value={username}
								className={classes.detailRow}
								editable={editable}
								onChangeText={setUsername}
								style={style.oddRow}
							/>
							<OrderDetailRow
								label={strings?.phone}
								value={phone}
								className={classes.detailRow}
								editable={editable}
								onChangeText={setPhone}
							/>
							<OrderDetailRow
								label={strings?.identificationNumber}
								value={identificationNumber}
								className={classes.detailRow}
								editable={editable}
								onChangeText={setIdentificationNumber}
							/>
							<OrderDetailRow
								label={strings?.collectRegionDescription}
								value={collectRegionDescription}
								className={classes.detailRow}
								editable={editable}
								onChangeText={setCollectRegionDescription}
							/>

							{editable ? (
								<div
									className="flex flex-row  items-center min-h-14 p-3"
									style={{
										...style.oddRow,
										display: "flex",
									}}
								>
									<p>{strings?.address}:</p>
									<PlaceAutocomplete
										onAddressChange={handleChange}
										country={countryCode ?? ""}
										ref={autocompleteRef}
									/>
								</div>
							) : (
								<OrderDetailRow
									label={strings?.address}
									value={address}
									className={classes.detailRow}
									// editable={editable}
									// onChangeText={setAddress}
									style={style.oddRow}
								/>
							)}
							<OrderDetailRow
								label={strings?.city}
								value={city}
								className={classes.detailRow}
							/>
							<OrderDetailRow
								label={strings?.country}
								value={country}
								className={classes.detailRow}
								style={style.oddRow}
							/>
							<OrderDetailRow
								label={strings?.businessName}
								value={businessName}
								className={classes.detailRow}
								editable={editable}
								onChangeText={setBusinessName}
							/>
							<OrderDetailRow
								label={strings?.businessNumber}
								value={businessNumber}
								className={classes.detailRow}
								editable={editable}
								onChangeText={setBusinessNumber}
								style={style.oddRow}
							/>
							<OrderDetailRow
								label={strings?.businessPhoneNumber}
								value={businessPhoneNumber}
								className={classes.detailRow}
								editable={editable}
								onChangeText={setBusinessPhoneNumber}
							/>
							<OrderDetailRow
								label={strings?.salesProfit}
								className={classes.detailRow}
								style={style.oddRow}
							/>
							<OrderDetailRow
								label={strings?.activeAccount}
								value={
									status === 1 ? strings?.yes : strings?.no
								}
								className={classes.detailRow}
							/>
							<OrderDetailRow
								label={strings?.registrationDate}
								value={createdAt}
								className={classes.detailRow}
								style={style.oddRow}
							/>
							{editable && (
								<div>
									<OrderDetailRow
										label={strings?.password}
										value={password}
										className="driverDetailRow"
										editable={editable}
										onChangeText={setPassword}
										type="password"
									/>
									<OrderDetailRow
										label={strings?.confirmPassword}
										value={confirmPassword}
										className="driverDetailRow"
										editable={editable}
										style={style.oddRow}
										onChangeText={setConfirmPassword}
										type="password"
									/>

									<OrderDetailRow
										label={strings?.cashPaymentLabel}
										value={cash}
										bool={editable}
										editable={editable}
										checked={cash}
										onCheck={changeSwitch}
									/>
									<OrderDetailRow
										label={strings?.bankPaymentLabel}
										value={bank}
										style={style.oddRow}
										bool={editable}
										editable={editable}
										checked={bank}
										onCheck={changeSwitch}
									/>
									<OrderDetailRow
										label={strings?.requiresDeliveryCode}
										value={requiresDeliveryCode}
										bool={editable}
										editable={editable}
										checked={requiresDeliveryCode}
										onCheck={changeSwichForDeliverCode}
									/>
									<OrderDetailRow
										label={strings?.deliveryCodeFee}
										value={deliveryCodeFee}
										style={style.oddRow}
										editable={editable}
										onChangeText={changeDeliverCodeFee}
									/>
								</div>
							)}
							<div className="flex flex-row flex-wrap gap-4 pt-3 p-4">
								<Button
									icon={<FormOutlined />}
									onClick={() => setEditable(true)}
								>
									{" "}
									{strings?.editAccountBtn}
								</Button>
								<Button
									icon={<MinusCircleOutlined />}
									onClick={() => setOpenBlockingModal(true)}
								>
									{strings?.blockAccountBtn}
								</Button>
							</div>
						</div>
						{/*
						 * The column beside the client info panel: balance cards, then the
						 * success-rate chart, then orders by country — each on its own row.
						 */}
						<div className={classes.balanceColumn}>
							{/*
							 * One card PER CURRENCY, side by side, at identical size: the LEK
							 * account and the EUR account, each holding its own obligation and
							 * turnover.
							 *
							 * The seller holds two independent debts — an order is settled in the
							 * currency it was registered in, and nothing is ever converted between
							 * them — so the split has to be the outermost thing on screen, not a
							 * badge the reader checks per figure. Earlier versions got this wrong
							 * in both directions: first EUR small beneath LEK, which read as a
							 * footnote on one number; then a card per metric per currency, which
							 * said the same two things four times and left the reader matching
							 * badges.
							 *
							 * Off PostmanAL there is one currency, so there is one card.
							 */}
							<div
								className={`${classes.cards} ${
									dualCurrencyEnabled ? classes.cardsDual : ""
								}`}
							>
								{balanceCards.map((card) => (
									<CurrencyBalanceCard
										key={String(card.currencyId)}
										currencyId={card.currencyId}
										sections={card.sections}
									/>
								))}
							</div>
							<div className={classes.clientChartOrdersContainer}>
								<div className={classes.clientChart}>
									<h1 className={classes.successRate}>
										{strings?.successRate}
									</h1>
									<div className={classes.chartInfo}>
										<div className={classes.firstChartInfo}>
											{percentages?.map((status: any) => (
												<li key={status?.id}>
													{status?.description}:{" "}
													{status?.percentage}%
												</li>
											))}
										</div>
										<div
											className={classes.secondChartInfo}
										>
											<ResponsiveContainer
												width="100%"
												height={200}
											>
												<PieChart>
													<Pie
														data={filteredOrders.map(
															(order, index) => ({
																id: index,
																value: order.numberOfOrders,
																color: colorMapping[
																	order.description as keyof typeof colorMapping
																],
															})
														)}
														cx="50%"
														cy="50%"
														outerRadius={80}
														fill="#8884d8"
														dataKey="value"
													>
														{dataChart.map(
															(
																entry: any,
																index: any
															) => (
																<Cell
																	key={`cell-${index}`}
																	fill={
																		entry.color
																	}
																/>
															)
														)}
													</Pie>
												</PieChart>
											</ResponsiveContainer>
										</div>
									</div>
								</div>
								<div
									className={classes.ordersByCountryContainer}
								>
									<List
										header={
											<p className={classes.ordersTitle}>
												{strings?.orderCountByCountry}
											</p>
										}
										className={classes.ordersByCountry}
										dataSource={tableOrderByCountries}
										renderItem={(item: any, index) => (
											<List.Item
												className={classes.tableOrders}
											>
												<p className={classes.country}>
													{item.country}
												</p>
												<p
													className={
														classes.orderCount
													}
												>
													{item.orderCount} (
													{item.percentage}%)
												</p>
											</List.Item>
										)}
									/>
								</div>
							</div>
						</div>
					</div>

					<div className={classes.secondContainer}>
						<div className={classes.settlementContainer}>
							<h1 className={classes.settlementTitle}>
								<b>{strings?.settlements}</b>
								<Button
									className={classes.buttonExcel}
									onClick={onExportSelectedSettlement}
								>
									<ExportIcon />
									{strings?.exportExcel}
								</Button>
							</h1>
							<div
								className="ag-theme-alpine"
								style={{
									// Grows with the data between two and five rows. Fixed at 333px
									// the panel was mostly empty for a client with one settlement,
									// which reads as "failed to load" rather than "nothing here".
									// 49px header + rowHeight (60) per row.
									height:
										Math.min(
											Math.max(
												settlementData?.length ?? 0,
												2
											),
											5
										) *
											60 +
										49,
									width: "100%",
								}}
							>
								<AgGridReact
									columnDefs={columnDefs2}
									rowData={settlementData}
									ref={settlementGridRef}
									defaultColDef={defaultColDef}
									enableRangeSelection={true}
									cacheQuickFilter={true}
									onCellClicked={onSettlementGridClicked}
									rowHeight={60}
									getRowStyle={getRowStyle}
									rowSelection="single"
								/>
							</div>
							<div
								className="ag-theme-alpine"
								style={{ display: "none" }}
							>
								<AgGridReact
									columnDefs={settlementDetailsColumns}
									rowData={settlementDetails}
									ref={settlementDetailsGridRef}
									defaultColDef={defaultColDef}
									enableRangeSelection={true}
									cacheQuickFilter={true}
									rowHeight={60}
									getRowStyle={getRowStyle}
									rowSelection="single"
								/>
							</div>
						</div>
					</div>
					<div className={classes.thirdContainer}>
						<div className={classes.filterThirdContainer}>
							<p className={classes.filterTitle}>
								{strings?.confirmOrderBtn}
							</p>
							<div className={classes.inputsContainer}>
								<DefaultInput
									className={classes.input}
									label={strings?.searchCode}
									prefix={<CodeIcon />}
									placeholder={strings?.searchCode}
								/>
								<DefaultInput
									className={classes.input}
									label={strings?.searchText}
									prefix={<SearchIcon />}
									placeholder={strings?.searchText}
								/>
								<DefaultSelect
									className={classes.input}
									label={strings?.status}
									flexDirection="col"
									alignCenter="start"
									placeholder={strings?.select}
								/>
								<DefaultSelect
									className={classes.input}
									label={strings?.country}
									flexDirection="col"
									alignCenter="start"
									placeholder={strings?.select}
								/>
								<DefaultSelect
									className={classes.input}
									label={strings?.city}
									flexDirection="col"
									alignCenter="start"
									placeholder={strings?.select}
								/>
							</div>
							<Divider />
							<div className="flex flex-wrap justify-between items-center">
								{cashRegisterDropDown?.length > 2 && (
									<DefaultSelect
										label={
											strings?.inputs.cashRegister.label
										}
										placeholder={
											strings?.inputs.cashRegister
												.placeholder
										}
										width={355}
										options={cashRegisterDropDown}
										onChange={(value) =>
											setCashRegisterId(value)
										}
										allowClear={true}
										onClear={() => setCashRegisterId(0)}
										flexDirection="col"
										alignCenter="start"
										className={classes.input}
									/>
								)}
								<Button
									className={classes.buttonExcel}
									onClick={onExportSelectedTableColumns}
								>
									<ExportIcon />
									{strings?.exportExcel}
								</Button>
							</div>
							<div className={classes.settlementOrder}>
								<div className={classes.settlementOrderInputs}>
									<DefaultInput
										label={`${strings?.settlementValue} (${currencySymbol()})`}
										underLabel={` ${
											storagePayment && !isBankPayment
												? "* " +
												  strings?.storageCostIncluded
												: ""
										}`}
										className={classes.input}
										value={settlementSum}
									/>
									{/* A separate input, never one merged total: the two halves are
									    paid out as two cash-register transactions and each is capped
									    by its own balance. */}
									{dualCurrencyEnabled && (
										<DefaultInput
											label={`${strings?.settlementValue} (${currencySymbol(
												CURRENCY_EUR
											)})`}
											className={classes.input}
											value={settlementSumEur ?? 0}
										/>
									)}
									{isBankPayment && (
										<div>
											<DefaultInput
												label={
													strings?.transportOrdervalue
												}
												underLabel={` ${
													storagePayment
														? "* " +
														  strings?.storageCostIncluded
														: ""
												}`}
												placeholder={
													strings?.willBePaidByBank
												}
												className={classes.input}
												value={transportSum}
											/>
										</div>
									)}
								</div>
								{canProcessSettlements && (
									<div
										className={
											classes.settlementOrderButtons
										}
									>
										<Button
											className={classes.button}
											onClick={
												handleFirstStepOfProccessing
											}
											disabled={
												(settlementSum === null ||
													settlementSum ===
														undefined ||
													selectedSettlementOrders.length ===
														0 ||
													Number(settlementSum) >
														Number(
															currentBalance ?? 0
														) ||
													// Each currency is capped by its own balance.
													Number(
														settlementSumEur ?? 0
													) >
														Number(
															currentBalanceEur ??
																0
														)) &&
												!exemptClientIds.includes(
													Number(clientId)
												)
											}
										>
											{strings?.confirmOrderBtn}
										</Button>
									</div>
								)}
							</div>
						</div>
						<div
							className="ag-theme-alpine"
							style={{ height: 500, width: "auto" }}
						>
							<AgGridReact
								rowData={tableData}
								columnDefs={tableColumns}
								defaultColDef={defaultColDef}
								onCellClicked={onCellClicked}
								rowSelection="multiple"
								ref={gridRef}
								enableRangeSelection={true}
								cacheQuickFilter={true}
								onSelectionChanged={onSelectionChange}
								onRowSelected={onRowSelected}
								rowHeight={50}
							/>
						</div>
						{/* <PaginationTable
							current={page}
							// pageSize={pageSize}
							total={tableData?.length}
							startOrderIndex={
								page > 0 ? (page - 1) * pageSize + 1 : page
							}
							endOrderIndex={Math.min(
								page * pageSize,
								tableData?.length
							)}
							onChange={onPageChange}
							onShowSizeChange={handlePageSizeChange}
						/> */}
					</div>
					<div className={classes.fourthContainer}>
						<div className={classes.hartContainer}>
							<div className="flex flex-wrap justify-between w-full gap-2  items-start">
								<h1 className="StatisticTitle">
									{strings?.hart}
								</h1>
								<Select
									value={selectedCountry}
									onChange={handleCountryChange}
									displayEmpty
									sx={{
										"& .css-1d3z3hw-MuiOutlinedInput-notchedOutline":
											{
												border: "white",
											},
										"& .css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input":
											{
												padding: 0,
											},
										"& .css-igs3ac": {
											border: "none",
										},
										color: "blue",
									}}
									IconComponent={ChevrovDown}
									className=" h-6"
								>
									{countries.map((country: any) => (
										<MenuItem
											key={country.value}
											value={country.value}
										>
											{country.label}
										</MenuItem>
									))}
								</Select>
							</div>
							<Map
								ordersCountByCity={ordersCountByCity}
								countryId={selectedCountry}
							/>
						</div>
						<div className={classes.orderByCities}>
							<List
								header={
									<div className="flex flex-wrap justify-between">
										<p className={classes.ordersTitle}>
											{strings?.orderCountByCity}
										</p>
										<Select
											value={selectedCountry}
											onChange={handleCountryChange}
											displayEmpty
											sx={{
												"& .css-1d3z3hw-MuiOutlinedInput-notchedOutline":
													{
														border: "white",
													},
												color: "blue",
											}}
											IconComponent={ChevrovDown}
										>
											{countries.map((country: any) => (
												<MenuItem
													key={country.value}
													value={country.value}
												>
													{country.label}
												</MenuItem>
											))}
										</Select>
									</div>
								}
								bordered
								dataSource={filteredCities}
								renderItem={(item: any) => (
									<List.Item
										key={item.city || item.id}
										className={classes.tableOrders}
									>
										<p className={classes.country}>
											{item.city}
										</p>
										<p className={classes.orderCount}>
											{item.orderCount} ({item.percentage}
											%)
										</p>
									</List.Item>
								)}
							>
								{filteredCities.length === 0 && (
									<div className={classes.noOrdersText}>
										{strings?.noOrders}
									</div>
								)}
							</List>
						</div>
					</div>
					<Divider />
					<div className={classes.buttonDiv}>
						<Button
							className={classes.buttonCancel}
							onClick={() => setEditable(false)}
						>
							{strings?.cancelBtn}
						</Button>
						<Button
							className={classes.buttonSave}
							onClick={saveClientChanges}
						>
							{strings?.saveBtn}
						</Button>
					</div>
				</div>
			</div>
			<Modal
				open={openSettlementProcessingModal}
				onCancel={() => setOpenSettlementProcessingModal(false)}
				onOk={settlementProcessing1}
				title={strings?.confirmSettlementProcessing}
				confirmLoading={isProcessing}
				// Nothing may close the dialog while the settlement is being written.
				closable={!isProcessing}
				maskClosable={!isProcessing}
				keyboard={!isProcessing}
				okButtonProps={{
					className: "bg-blue-500",
					disabled: isProcessing,
				}}
				cancelButtonProps={{ disabled: isProcessing }}
			>
				<p>{strings?.confirmSettlementProcessing}</p>
			</Modal>
			<Modal
				open={bankTransportPaymentModal}
				onCancel={() => setBankTransportPaymentModal(false)}
				onOk={insertEarning}
				title={strings?.confirmBankTransportPaymentProcessing}
				okButtonProps={{
					className: "bg-blue-500",
					disabled: insertEarningDisabled,
				}}
			>
				<p>{strings?.confirmBankTransportPaymentProcessing}</p>
			</Modal>
			<Modal
				open={openSettlementClientWithdrawnModal}
				onCancel={() => {
					setOpenSettlementClientWithdrawnModal(false);
					setSettlementId(null);
				}}
				onOk={deliverSettlementToClient}
				title={<ExclamationCircleFilled size={160} />}
				okButtonProps={{
					className: "bg-blue-500",
					disabled: deliverSettlementToClientDisabled,
				}}
			>
				<p>{strings?.confirmDeliverSettlementToClient}</p>
			</Modal>
			{/* <Modal
				open={openConfirmModal}
				onCancel={() => setOpenConfirmModal(false)}
				onOk={confirmRollBack}
				title={<ExclamationCircleFilled size={160} />}
				okButtonProps={{ className: "bg-blue-500" }}
			>
				<p>{strings?.confirmRollBack}</p>
			</Modal> */}
			<Modal
				open={openSettlementOrderDetailsModal}
				title={`${strings?.orderDetails} Nr.${settlementDetailsId}`}
				width={1000}
				onCancel={cancelModal}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
			>
				<>
					<div>
						<div className="flex justify-end">
							<Button
								className={classes.buttonExcel}
								onClick={onExportSelectedSettlementOrder}
							>
								<ExportIcon />
								{strings?.exportExcel}
							</Button>
						</div>
						<Divider />
						<div
							className="ag-theme-alpine"
							style={{
								height: 600,
								width: "100%",
								overflowX: "auto",
							}}
						>
							<AgGridReact
								rowData={settlementOrderData}
								columnDefs={settlementOrderTableColumns}
								defaultColDef={defaultColDef}
								suppressCopyRowsToClipboard={true}
								defaultExcelExportParams={
									defaultExcelExportSettings
								}
								domLayout="autoHeight"
								ref={settlementOrderGridRef}
								enableRangeSelection={true}
								cacheQuickFilter={true}
								rowHeight={60}
							/>
						</div>
					</div>
				</>
			</Modal>
			<Modal
				open={openBlockingModal}
				onCancel={() => setOpenBlockingModal(false)}
				onOk={confirmBlocking}
				title={strings?.confirmUserBlockingTitle}
				okButtonProps={{ className: "bg-blue-500" }}
			>
				<p>{strings?.confirmUserBlocking}</p>
			</Modal>

			<Modal
				open={openEarningDocumentsModal}
				title={strings?.earningDocumentsModalTitle}
				width={900}
				onCancel={closeEarningDocumentsModal}
				// cancelButtonProps={{ style: { display: "none" } }}
				footer={() => (
					<div className="flex  flex-row items-end justify-end ">
						<Button
							size="large"
							icon={<CloseOutlined />}
							onClick={closeEarningDocumentsModal}
						>
							Cancel
						</Button>
						<Button
							className="registerButton"
							onClick={() =>
								setOpenSettlementProcessingModal(true)
							}
						>
							Vazhdo procesimin
						</Button>
					</div>
				)}
			>
				<>
					<div>
						<Divider />
						<div>
							{/* One column per currency on PostmanAL: a settlement is partitioned by
							    the currency each order was REGISTERED in and paid out as two
							    separate cash-register transactions. Never a combined total (W-D16). */}
							<SettlementBreakdown
								columns={settlementBreakdown}
								refusalCount={earningDocumentsData?.length ?? 0}
								footnote={
									storagePayment && !isBankPayment
										? `* ${strings?.storageCostIncluded}`
										: undefined
								}
								bankTransport={
									isBankPayment
										? {
												value: Number(
													transportSum ?? 0
												),
												withSymbol:
													!dualCurrencyEnabled,
												footnote: storagePayment
													? `* ${strings?.storageCostIncluded}`
													: undefined,
										  }
										: undefined
								}
							/>
							{/* Refusals and exchanges are always part of the settlement — no opt-out. */}
							<Alert
								className="mt-5"
								type="info"
								showIcon
								message={
									<span className="text-lg font-semibold">
										{strings?.earningsAlwaysIncludedTitle ??
											"Të gjitha refuzimet dhe ndërrimet më poshtë do të zbriten nga barazimi"}
									</span>
								}
								description={
									<span className="text-base">
										{strings?.earningsAlwaysIncludedInfo ??
											"Borxhi i klientit për porositë e refuzuara ose të ndërruara në listën më poshtë zbritet automatikisht nga Vlera e Barazimit. Këto porosi përfshihen detyrimisht në barazim dhe nuk mund të largohen."}
									</span>
								}
							/>
						</div>
						<div className="flex justify-end m-2">
							<Button
								className={classes.buttonExcel}
								onClick={onExportSelectedEarningDocuments}
							>
								<ExportIcon />
								{strings?.exportExcel}
							</Button>
						</div>
						<div
							className="ag-theme-alpine"
							style={{
								height: 600,
								width: "100%",
								overflowX: "auto",
							}}
						>
							<AgGridReact
								rowData={earningDocumentsData}
								columnDefs={earningDocumentsTableColumns}
								defaultColDef={defaultColDef}
								suppressCopyRowsToClipboard={true}
								defaultExcelExportParams={
									defaultExcelExportSettings
								}
								domLayout="autoHeight"
								ref={earningDocumentsGridRef}
								enableRangeSelection={true}
								cacheQuickFilter={true}
								rowHeight={60}
							/>
						</div>
					</div>
				</>
			</Modal>
		</>
	);
};

export default ClientDetails;

const style = {
	oddRow: { backgroundColor: "#F9F8FC" },
};
